import React from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  Center,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import { FiPlus } from 'react-icons/fi'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useGroups } from '@/hooks/useGroups'
import CreateGroupModal from '@/components/Groups/CreateGroupModal'
import EditGroupModal from '@/components/Groups/EditGroupModal'
import DeleteGroupModal from '@/components/Groups/DeleteGroupModal'
import GroupMembersModal from '@/components/Groups/GroupMembersModal'
import GroupCard from '@/components/Groups/GroupCard'

export default function GroupsPage() {
  const { t } = useAppTranslation(['admin', 'common'])
  const subtitleColor = useColorModeValue('gray.500', 'gray.400')
  const {
    groups,
    membersById,
    isLoading,
    isAdmin,
    selectedGroup,
    createModal,
    editModal,
    deleteModal,
    membersModal,
    handleEdit,
    handleDelete,
    handleMembers,
    confirmDelete,
  } = useGroups()

  return (
    <Box p={6}>
      <HStack justify="space-between" align="flex-start" mb={6}>
        <VStack align="flex-start" spacing={1}>
          <Heading size="md">{t('groups.title', { ns: 'admin' })}</Heading>
          <Text fontSize="sm" color={subtitleColor}>
            {t('groups.subtitle', { ns: 'admin' })}
          </Text>
        </VStack>
        {isAdmin && (
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            size="sm"
            onClick={createModal.onOpen}
          >
            {t('groups.createGroup', { ns: 'admin' })}
          </Button>
        )}
      </HStack>

      {isLoading ? (
        <Center py={12}>
          <Spinner size="lg" color="brand.500" thickness="3px" />
        </Center>
      ) : groups.length === 0 ? (
        <Card borderRadius="xl" boxShadow="sm">
          <CardBody>
            <Center py={12}>
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="medium" color="gray.600">
                  {t('groups.noGroups', { ns: 'admin' })}
                </Text>
              </VStack>
            </Center>
          </CardBody>
        </Card>
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
          {groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              members={membersById[group.id]}
              isAdmin={isAdmin}
              onManageMembers={() => handleMembers(group)}
              onEdit={() => handleEdit(group)}
              onDelete={() => handleDelete(group)}
            />
          ))}
        </SimpleGrid>
      )}

      <CreateGroupModal isOpen={createModal.isOpen} onClose={createModal.onClose} />

      {selectedGroup && (
        <>
          <EditGroupModal
            isOpen={editModal.isOpen}
            onClose={editModal.onClose}
            group={selectedGroup}
          />
          <DeleteGroupModal
            isOpen={deleteModal.isOpen}
            onClose={deleteModal.onClose}
            group={selectedGroup}
            onConfirm={confirmDelete}
          />
          <GroupMembersModal
            isOpen={membersModal.isOpen}
            onClose={membersModal.onClose}
            group={selectedGroup}
          />
        </>
      )}
    </Box>
  )
}
